import type { Account } from "@/hooks/api/use-accounts";
import { buildFilterGroupFromColumnState } from "@/lib/filters";
import type { QueryParams } from "@/lib/process-query-params";
import type { SortDirection, UnifiedTableColumn } from "@/types/table-column";
import { type BaseListFilters, useListFilters } from "./use-list-filters";

// Default sort settings
const DEFAULT_SORT_COLUMN = "opportunity_count";
const DEFAULT_SORT_DIRECTION: SortDirection = "desc";

// SessionStorage key
const ACCOUNT_LIST_FILTERS_KEY = "accounts::list::filters";

// Interface for filter configuration
export interface AccountListFilters extends BaseListFilters {
  /**
   * Type identifier for Account specific filters.
   * @private Internal use only - not meant to be accessed directly
   */
  readonly _type?: "account";
}

/**
 * Hook for managing account list filters with organization-scoping
 */
export function useAccountListFilters() {
  // Helper function to build query params
  const buildQueryParams = (
    filters: AccountListFilters,
    options: {
      searchTerm?: string;
      columns?: UnifiedTableColumn<Account>[];
      limit?: number;
    },
  ): QueryParams => {
    const { searchTerm, columns = [], limit = 25 } = options;

    let sortCol = filters.sortColumn || DEFAULT_SORT_COLUMN;

    // Fallback if sortCol no longer exists in columns (e.g., custom field deselected)
    const validSortKeys = columns.map((c) => c.key);
    if (!validSortKeys.includes(sortCol)) {
      sortCol = DEFAULT_SORT_COLUMN;
    }

    const sortDir = filters.sortDirection || DEFAULT_SORT_DIRECTION;

    // Find column definition for the current sort to get nullSort
    const column = columns.find((col) => col.key === sortCol);

    // Build base params
    const params: QueryParams = {
      sort_by: sortCol,
      sort_direction: sortDir === null ? undefined : sortDir,
      null_sort: column?.nullSort,
      search: searchTerm || filters.search || undefined,
      limit,
    };

    // -----------------------------------------------------------------
    // Build StructuredFilter array ➔ FilterGroup
    // -----------------------------------------------------------------

    const validColumnKeys = new Set(columns.map((c) => c.key.toString()));
    const group = buildFilterGroupFromColumnState(filters, validColumnKeys);
    if (group) params.filters = JSON.stringify(group);

    return params;
  };

  return useListFilters<
    AccountListFilters,
    UnifiedTableColumn<Account>,
    QueryParams
  >({
    storageKey: ACCOUNT_LIST_FILTERS_KEY,
    defaultSortColumn: DEFAULT_SORT_COLUMN,
    defaultSortDirection: DEFAULT_SORT_DIRECTION,
    buildQueryParams,
  });
}
